import { createTheme } from '@mui/material';
import { corePalette } from './corePalette';
import { colors } from '../utils/colors';
import { layoutTokens, sharedDataGridRootStyle, sharedNonColorComponents } from './layoutTokens';
import { bluePalette } from './bluePalette';
import { getEasterComponentOverrides } from './easterTheme';

const easterComponentOverrides = getEasterComponentOverrides(false);

export const coreTheme = createTheme({
    typography: {
        fontFamily: 'Source Sans Pro, -apple-system, Segoe UI, Helvetica Neue, sans-serif'
    },
    shape: {
        borderRadius: layoutTokens.borderRadiusBase
    },
    palette: {
        ...corePalette,
        primary: { main: colors.blue.i2x_flashy },
        secondary: { main: colors.blue.i2x_flashy },
        gray: { main: colors.gray.cold_gray }
    },
    components: {
        ...sharedNonColorComponents,
        MuiInput: {
            styleOverrides: {
                root: {
                    minHeight: layoutTokens.filterControlHeight,
                    fontSize: `${layoutTokens.controlFontSize}px`,
                    boxSizing: 'border-box',
                    border: `1px solid ${corePalette.filterBorderColor}`,
                    borderRadius: corePalette.contentFilterInputBorderRadius || '4px',
                    backgroundColor: corePalette.contentFilterInputBackground,
                    padding: corePalette.contentFilterInputPadding || '7px 8px',
                    paddingRight: 32,
                    '&:before, &:after': {
                        borderBottom: 'none !important'
                    },
                    '&:hover': {
                        borderColor: corePalette.dateRangeLabelHighlighted
                    },
                    '&.Mui-focused': {
                        borderColor: corePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiSelect-select': {
                        minHeight: '20px !important',
                        fontSize: `${layoutTokens.controlFontSize}px`,
                        display: 'flex',
                        alignItems: 'center'
                    },
                    '& .MuiSelect-icon': {
                        right: 7
                    }
                }
            }
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small'
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    minHeight: layoutTokens.filterControlHeight,
                    color: corePalette.dateRangeText,
                    backgroundColor: corePalette.contentFilterInputBackground,
                    borderRadius: corePalette.contentFilterInputBorderRadius || `${layoutTokens.borderRadiusControl}px`,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: corePalette.borderColor
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: corePalette.dateRangeLabelHighlighted
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: corePalette.dateRangeLabelHighlighted
                    },
                    '&.Mui-disabled': {
                        backgroundColor: corePalette.contentFilterInputBackground
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: corePalette.filterBorderColor
                    },
                    '& .MuiSvgIcon-root': {
                        color: corePalette.downArrowColor
                    }
                },
                input: {
                    fontSize: layoutTokens.controlFontSize,
                    '&.Mui-disabled': {
                        WebkitTextFillColor: corePalette.dateRangeText,
                        color: corePalette.dateRangeText,
                        opacity: 0.75
                    }
                },
                inputAdornedEnd: {
                    color: corePalette.dateRangeText
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: corePalette.dateRangeText,
                    '& .MuiSvgIcon-root': {
                        color: corePalette.downArrowColor
                    }
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        minHeight: layoutTokens.filterControlHeight,
                        backgroundColor: corePalette.contentFilterInputBackground,
                        borderRadius:
                            corePalette.contentFilterInputBorderRadius || `${layoutTokens.borderRadiusControl}px`,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: corePalette.filterBorderColor
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: corePalette.dateRangeLabelHighlighted
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: corePalette.dateRangeLabelHighlighted
                        }
                    },
                    '& .MuiInputBase-root': {
                        color: corePalette.contentPaperText
                    },
                    '& .MuiInputBase-input': {
                        color: corePalette.contentPaperText,
                        WebkitTextFillColor: corePalette.contentPaperText
                    },
                    '& .MuiInputLabel-root': {
                        color: corePalette.inputLabelColor
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: corePalette.dateRangeLabelHighlighted
                    },
                    '& .MuiInputBase-root.MuiInput-root': {
                        minHeight: layoutTokens.filterControlHeight,
                        boxSizing: 'border-box',
                        border: `1px solid ${corePalette.filterBorderColor}`,
                        borderRadius: corePalette.contentFilterInputBorderRadius || '4px',
                        backgroundColor: corePalette.contentFilterInputBackground,
                        padding: corePalette.contentFilterInputPadding || '7px 8px',
                        paddingRight: 32,
                        color: corePalette.contentPaperText,
                        '&:before, &:after': {
                            borderBottom: 'none !important'
                        }
                    },
                    '& .MuiSelect-select': {
                        minHeight: '20px !important',
                        display: 'flex',
                        alignItems: 'center'
                    },
                    '& .MuiAutocomplete-inputRoot': {
                        minHeight: layoutTokens.filterControlHeight
                    },
                    '& .MuiSvgIcon-root': {
                        color: corePalette.downArrowColor
                    }
                },
                paper: {
                    backgroundColor: corePalette.dropdownMenuPaperBg,
                    color: corePalette.contentPaperText,
                    border: `1px solid ${corePalette.filterPopupBorder}`,
                    borderRadius: `${layoutTokens.borderRadiusControl}px`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                },
                listbox: {
                    backgroundColor: 'transparent'
                },
                option: {
                    color: corePalette.contentPaperText,
                    '&[aria-selected="true"]': {
                        backgroundColor: corePalette.dropdownMenuItemSelectedBg
                    },
                    '&.Mui-focused': {
                        backgroundColor: corePalette.dropdownMenuItemSelectedHoverBg
                    }
                },
                groupLabel: {
                    backgroundColor: 'transparent',
                    color: corePalette.contentPaperText
                },
                popupIndicator: {
                    color: corePalette.contentPaperText
                },
                clearIndicator: {
                    color: corePalette.contentPaperText
                }
            }
        },
        MuiPopper: {
            styleOverrides: {
                root: {
                    zIndex: 2000
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: corePalette.modalBackdropBg
                    }
                },
                paper: {
                    borderRadius: layoutTokens.borderRadiusPanel,
                    padding: 0,
                    backdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    WebkitBackdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    background: corePalette.contentPaperBackground,
                    color: corePalette.contentPaperText,
                    border: `1px solid ${corePalette.contentPaperBorder}`,
                    '& .MuiDialogTitle-root': {
                        color: corePalette.contentPaperText
                    },
                    '& .MuiDialogContent-root': {
                        color: corePalette.contentPaperText
                    },
                    '& .MuiDialogContentText-root': {
                        color: corePalette.contentPaperText
                    }
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: corePalette.contentPaperText,
                    backgroundColor: 'transparent',
                    padding: '16px 24px'
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    color: corePalette.contentPaperText,
                    backgroundColor: 'transparent',
                    padding: '12px 24px 24px',
                    '&.MuiDialogContent-dividers': {
                        borderTop: `1px solid ${corePalette.surfaceBorderSubtle}`,
                        borderBottom: 'none'
                    }
                }
            }
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    color: corePalette.contentPaperText
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    borderTop: `1px solid ${corePalette.surfaceBorderSubtle}`,
                    padding: '12px 24px',
                    minHeight: 64,
                    '& .MuiButton-root:not(.MuiButton-colorError)': {
                        color: corePalette.buttonAccentBg,
                        border: `1px solid ${corePalette.buttonAccentBg}`,
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '& .MuiSvgIcon-root': {
                            color: 'inherit'
                        },
                        '&:hover': {
                            backgroundColor: corePalette.buttonAccentSoftBg,
                            borderColor: corePalette.buttonAccentBg,
                            boxShadow: 'none'
                        },
                        '&.Mui-disabled': {
                            color: corePalette.buttonAccentDisabledText,
                            borderColor: corePalette.buttonAccentDisabledBorder,
                            backgroundColor: corePalette.buttonAccentSoftDisabledBg
                        }
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: layoutTokens.borderRadiusPanel,
                    backdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    WebkitBackdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                    border: easterComponentOverrides.cardBorder
                }
            }
        },
        MuiDataGrid: {
            defaultProps: {
                rowHeight: layoutTokens.tableRowHeight,
                columnHeaderHeight: layoutTokens.tableHeaderHeight
            },
            styleOverrides: {
                root: {
                    ...sharedDataGridRootStyle,
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: corePalette.surfaceOverlay
                    },
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: corePalette.surfaceOverlay
                    },
                    '& .MuiDataGrid-topContainer': {
                        backgroundColor: corePalette.surfaceOverlay
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                containedError: {
                    color: '#8f1d2c',
                    backgroundColor: 'rgba(220, 53, 69, 0.14)',
                    border: '1px solid rgba(220, 53, 69, 0.34)',
                    boxShadow: 'none',
                    '& .MuiSvgIcon-root': {
                        color: 'inherit'
                    },
                    '&:hover': {
                        boxShadow: 'none',
                        backgroundColor: 'rgba(220, 53, 69, 0.22)',
                        borderColor: 'rgba(220, 53, 69, 0.44)'
                    }
                },
                outlinedError: {
                    color: '#8f1d2c',
                    borderColor: 'rgba(220, 53, 69, 0.4)',
                    backgroundColor: 'rgba(220, 53, 69, 0.08)',
                    '& .MuiSvgIcon-root': {
                        color: 'inherit'
                    },
                    '&:hover': {
                        borderColor: 'rgba(220, 53, 69, 0.52)',
                        backgroundColor: 'rgba(220, 53, 69, 0.14)'
                    }
                },
                textError: {
                    color: '#8f1d2c',
                    '& .MuiSvgIcon-root': {
                        color: 'inherit'
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(220, 53, 69, 0.08)'
                    }
                }
            }
        }
    }
});
